import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Globe, Target, Sparkles, Gauge, Wand2, Tag, Users, Building2, LayoutDashboard, Rocket,
  ArrowRight, ArrowUpRight, Check,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/lib/motion";
import { PLATFORM_HERO, PLATFORM_STATS, PLATFORM_GROUPS } from "@/data/platformFeatures";

const ICONS = { Globe, Target, Sparkles, Gauge, Wand2, Tag, Users, Building2, LayoutDashboard, Rocket };

export default function FeaturesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Platform & Features | WebsiteTech.pro";
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-24" data-testid="features-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0b1220] text-white">
          <div className="absolute -right-24 -top-24 w-96 h-96 bg-brand/30 blur-3xl rounded-full" />
          <div className="absolute -left-32 bottom-0 w-96 h-96 bg-indigo-500/20 blur-3xl rounded-full" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-blue-300">{PLATFORM_HERO.overline}</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 leading-[1.05] max-w-4xl">
              {PLATFORM_HERO.title}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mt-6 leading-relaxed max-w-3xl">{PLATFORM_HERO.subtitle}</p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/audit" data-testid="features-audit-cta" className="bg-white text-slate-900 font-semibold rounded-xl px-6 py-3.5 text-sm flex items-center gap-2 hover:bg-slate-100 transition-colors">
                Run a free audit <ArrowRight size={15} />
              </Link>
              <Link to="/growth-advisor" className="border border-white/25 text-white rounded-xl px-6 py-3.5 text-sm flex items-center gap-2 hover:bg-white/10 transition-colors">
                <Sparkles size={15} /> Ask the AI Growth Advisor
              </Link>
              <Link to="/demos" data-testid="features-demos-cta" className="border border-white/25 text-white rounded-xl px-6 py-3.5 text-sm flex items-center gap-2 hover:bg-white/10 transition-colors">
                See industry demos <ArrowUpRight size={15} />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 border-t border-white/10 pt-10">
              {PLATFORM_STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-heading text-3xl md:text-4xl font-extrabold text-white">{s.value}</div>
                  <div className="text-sm text-slate-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Capability groups */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 space-y-16">
          {PLATFORM_GROUPS.map((g, gi) => {
            const Icon = ICONS[g.icon] || Sparkles;
            return (
              <Reveal key={g.id}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid={`feature-group-${g.id}`}>
                  {/* Left: title */}
                  <div className="lg:col-span-4">
                    <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${g.comingSoon ? "bg-amber-100 text-amber-600" : "bg-brand/10 text-brand"}`}>
                      <Icon size={22} />
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">{g.title}</h2>
                    </div>
                    {g.comingSoon && <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full">Planned</span>}
                    <p className="text-slate-500 mt-3">{g.tagline}</p>
                    {g.link && (
                      <Link to={g.link.to} data-testid={`feature-link-${g.id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2.5 transition-all">
                        {g.link.label} <ArrowUpRight size={15} />
                      </Link>
                    )}
                  </div>

                  {/* Right: feature cards */}
                  <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {g.features.map((f) => (
                      <div key={f.title} className={`rounded-2xl border p-5 hover-lift ${g.comingSoon ? "border-dashed border-slate-300 bg-slate-50/60" : "border-slate-200 bg-white card-shadow"}`}>
                        <div className="flex items-start gap-2.5">
                          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${g.comingSoon ? "bg-amber-100 text-amber-600" : "bg-green-50 text-green-600"}`}>
                            <Check size={12} strokeWidth={3} />
                          </span>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-slate-900">{f.title}</h3>
                              {f.badge && <span className="text-[9px] font-bold uppercase tracking-wide bg-blue-100 text-brand px-1.5 py-0.5 rounded">{f.badge}</span>}
                            </div>
                            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{f.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {gi < PLATFORM_GROUPS.length - 1 && <div className="mt-16 border-t border-slate-100" />}
              </Reveal>
            );
          })}
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="rounded-3xl bg-slate-900 p-10 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-72 h-72 bg-brand/30 blur-3xl rounded-full" />
            <div className="relative">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Ready to put it all to work?</h2>
              <p className="text-slate-300 mt-4 max-w-2xl mx-auto text-lg">Start with a free lead-generation audit, or let the AI Growth Advisor build your plan in minutes.</p>
              <div className="flex flex-wrap justify-center gap-3 mt-8">
                <Link to="/audit" className="btn-primary rounded-xl px-7 py-3.5 text-sm flex items-center gap-2">Run a free audit <ArrowRight size={15} /></Link>
                <Link to="/pricing" className="border border-white/25 text-white rounded-xl px-7 py-3.5 text-sm flex items-center gap-2 hover:bg-white/10 transition-colors">See pricing</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
